import { useAppConfig } from '@plato/app-core/hooks/use-app-config';
import type { SrSalesOrderType } from '@plato/data/starrocks/types';
import { BaseConfirmationDialog } from '@plato/ui-legacy/components/dialog/confirmation-dialog';
import { DialogStateManager, useDialog } from '@plato/ui-legacy/components/dialog/dialog-provider';
import { RadioList, RadioListItem } from '@plato/ui-legacy/components/ui/button-radio-list';
import { useToast } from '@plato/ui-legacy/components/ui/use-toast';
import { RocketIcon } from 'lucide-react';
import { useState } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import { api } from '../../../utils/api.ts';
import { DraftOrderCreateDialog } from '../../sales-draft-new/views/create-draft-order-dialog.tsx';
import { useDetailQuoteOrder } from '../hooks/use-detail-quote-order.tsx';

type QuoteOrderReleaseDialogProps = {
    transactionId: string;
    type: SrSalesOrderType;
};

export const QuoteOrderReleaseDialog = DialogStateManager.create(
    ({ transactionId, type }: QuoteOrderReleaseDialogProps) => {
        const { remove } = useDialog();
        const { t } = useTranslation('quotes-orders-actions');
        const { toast } = useToast();
        const { canSendSalesOrderPdfByEmail } = useAppConfig();
        const [sendEmail, setSendEmail] = useState(canSendSalesOrderPdfByEmail ? '1' : '0');
        const apiUtils = api.useUtils();

        const { data } = useDetailQuoteOrder({ transactionId });

        const { mutate: release, isPending } = api.platoUser.command.salesOrder.order.release.useMutation({
            async onSuccess() {
                await Promise.all([
                    apiUtils.platoUser.getManyQuotesOrders.getAggregates.invalidate(),
                    apiUtils.platoUser.getManyQuotesOrders.getWithFilters.invalidate(),
                    apiUtils.platoUser.grid.getDetailQuoteOrder.get.invalidate({ id: transactionId }),
                ]);

                const components = {
                    linkTag: (
                        <div
                            className="text-blue-700 hover:underline"
                            onClick={() => {
                                void DialogStateManager.show(DraftOrderCreateDialog, {
                                    customerId: data?.header.customer_id,
                                    source: type === 'ORDER' ? 'order_detail' : 'quote_detail',
                                });
                            }}
                        />
                    ),
                };

                toast({
                    icon: 'success',
                    title:
                        type === 'ORDER'
                            ? t('ORDER__release_toast__success_title')
                            : t('QUOTE__release_toast__success_title'),
                    description:
                        type === 'ORDER' ? (
                            <Trans
                                t={t}
                                i18nKey="ORDER__release_toast__success_description"
                                components={components}
                            />
                        ) : (
                            <Trans
                                t={t}
                                i18nKey="QUOTE__release_toast__success_description"
                                components={components}
                            />
                        ),
                });

                remove();
            },
            onError(error) {
                toast({
                    icon: 'warning',
                    title: 'Error',
                    description: error.message,
                });
            },
        });

        const handleRelease = (): void => {
            release({
                sales_order_id: transactionId,
                should_send_email: Boolean(Number(sendEmail)),
            });
        };

        return (
            <BaseConfirmationDialog
                icon={RocketIcon}
                title={
                    type === 'ORDER'
                        ? t('ORDER__release_confirmation__title')
                        : t('QUOTE__release_confirmation__title')
                }
                description={
                    type === 'ORDER'
                        ? t('ORDER__release_confirmation__description')
                        : t('QUOTE__release_confirmation__description')
                }
            >
                <div className="flex w-full">
                    <RadioList defaultValue={sendEmail} onValueChange={setSendEmail}>
                        <RadioListItem
                            disabled={!canSendSalesOrderPdfByEmail}
                            value="1"
                            id="1"
                            label={t('send_pdf_by_email')}
                        />
                        <RadioListItem value="0" id="0" label={t('do_not_send_pdf_by_email')} />
                    </RadioList>
                </div>
                <BaseConfirmationDialog.Actions>
                    <BaseConfirmationDialog.RejectButton onClick={remove}>
                        {t('form__cancel', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.RejectButton>
                    <BaseConfirmationDialog.AcceptButton onClick={handleRelease} isLoading={isPending}>
                        {t('form__release', {
                            ns: 'common',
                        })}
                    </BaseConfirmationDialog.AcceptButton>
                </BaseConfirmationDialog.Actions>
            </BaseConfirmationDialog>
        );
    },
);
